import React from 'react';
import {
  isObjectLike,
  startCase,
} from 'lodash';
import { AuditLogAnyAction } from '../../../types/api/AuditLog';
import cx from 'classnames';
import { useStores } from '../../hooks/use-stores';
import {
  mapPropertyValue,
  PROPERTY_MAP,
} from './AuditLogHelper';

interface AuditLogDataChangeEntryProps {
  propertyKey: string;
  propertyValue: unknown;
  item: AuditLogAnyAction;
}

interface ValueBoxProps {
  value: unknown;
  className: string;
}

/**
 * Renders a value (which can be an array or a single item) inside a styled box.
 */
const ValueBox: React.FC<ValueBoxProps> = ({ value, className }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      // Render an empty array representation
      return <div className={className}>[]</div>;
    }
    // Render each array item with its index
    return (
      <>
        {value.map((item, index) => (
          <div key={index} className={`${className} ${className}--array`}>
            #{index}: {JSON.stringify(item)}
          </div>
        ))}
      </>
    );
  }
  if (isObjectLike(value)) {
    return <div className={className}><pre>{JSON.stringify(value, null, 2)}</pre></div>;
  }
  // Render a single value
  return <div className={className}>{JSON.stringify(value)}</div>;
};

/**
 * Displays the changes of a specific property between its old and new values in the audit log.
 */
export const AuditLogDataChangeEntry: React.FC<AuditLogDataChangeEntryProps> = ({
  propertyKey,
  propertyValue,
  item,
}) => {
  const mapped = PROPERTY_MAP[propertyKey];
  const { settingsStore } = useStores();

  const humanCasePropertyKey = mapped?.label ?? startCase(propertyKey);

  const mappedPropertyValueFrom = mapPropertyValue(propertyKey, propertyValue, settingsStore);

  const mappedPropertyValueTo = item.actionType === 'Update'
    ? mapPropertyValue(propertyKey, item.details.to[propertyKey], settingsStore)
    : undefined;

  if (mapped === null) {
    return null;
  }

  return (
    <div className="audit-log-data-content__entry">
      <span>{humanCasePropertyKey}:</span>
      {/* Render the original value */}
      <ValueBox
        value={mappedPropertyValueFrom}
        className={cx('audit-log-data-content__entry-box', {
          'audit-log-data-content__entry-box-red': item.actionType === 'Delete' || item.actionType === 'Update',
          'audit-log-data-content__entry-box-green': item.actionType === 'Create',
        })}
      />
      <i className="fa fa-arrow-right-long" />
      {/* Render the new value */}
      {item.actionType === 'Update' && (
        <ValueBox
          value={mappedPropertyValueTo}
          className="audit-log-data-content__entry-box audit-log-data-content__entry-box-green"
        />
      )}
    </div>
  );
};
